import { publicAxios, protectedAxios } from "@shared/src/utils/axios";
import type { PaymentMethod, Currency, Token, AccountPaymentConfig, Network } from "@sdk/shared/types/config.types";
import { handleServiceError } from "@shared/src/utils/error-handler";
import { mapNetworkToIcon, mapTokenToIcon } from "@sdk/utils/icons";

export const configService = {
  // Public endpoints (no auth required)
  getNetworks: async () => {
    return handleServiceError(async () => {
      const { data }: { data: Network[] } = await publicAxios.get<Network[]>("/configs/networks");
      if (!data) {
        throw new Error("Failed to fetch networks");
      }
      return data.map(network => mapNetworkToIcon(network));
    }, []);
  },

  getPaymentMethods: async () => {
    return handleServiceError(async () => {
      const { data } = await publicAxios.get<PaymentMethod[]>("/configs/payment-methods");
      if (!data) {
        throw new Error("Failed to fetch payment methods");
      }
      return data;
    }, []);
  },

  getCurrencies: async () => {
    return handleServiceError(async () => {
      const { data } = await publicAxios.get<Currency[]>("/configs/currencies");
      if (!data) {
        throw new Error("Failed to fetch currencies");
      }
      return data;
    }, []);
  },

  getTokens: async (networkId?: number) => {
    return handleServiceError(async () => {
      const { data } = await publicAxios.get<Token[]>(`/configs/tokens`, {
        params: networkId ? { networkId } : undefined,
      });
      if (!data) {
        throw new Error("Failed to fetch tokens");
      }
      return data.map(mapTokenToIcon);
    }, []);
  },

  // Protected endpoints (auth required)
  getAccountPaymentConfigs: async () => {
    return handleServiceError(async () => {
      const { data } = await protectedAxios.get<AccountPaymentConfig[]>("/configs/payment/configs");
      if (!data) {
        throw new Error("Failed to fetch payment configs");
      }
      return data.filter(config => config.isActive);
    }, []);
  },

  createPaymentConfig: async (payload: { account: string; accountName: string; methodId: string }) => {
    return handleServiceError(async () => {
      const { data } = await protectedAxios.post<AccountPaymentConfig>("/configs/payment/config", payload);
      if (!data) {
        throw new Error("Failed to create payment config");
      }
      return data;
    });
  },

  updatePaymentConfig: async (
    configId: number,
    payload: {
      account: string;
      accountName: string;
      extraInfo?: string;
      methodId: string;
    },
  ) => {
    return handleServiceError(async () => {
      const { data } = await protectedAxios.post<AccountPaymentConfig>(`/configs/payment/config/${configId}`, payload);
      if (!data) {
        throw new Error("Failed to update payment config");
      }
      return data;
    });
  },

  deletePaymentConfig: async (configId: number) => {
    return handleServiceError(async () => {
      const { data } = await protectedAxios.post<AccountPaymentConfig>(`/configs/payment/config/${configId}`, {
        isActive: false,
      });
      if (!data) {
        throw new Error("Failed to delete payment config");
      }
      return data;
    });
  },
};
